import { useMemo, useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Landmark,
  MapPin,
  Search,
  Users,
  Briefcase,
  Trophy,
  TrendingUp,
  Filter,
  X,
  AlertTriangle,
} from "lucide-react";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000)
    return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000)
    return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}

type TypeFilter = "all" | "public_admin" | "empresa" | "cliente_final";

const TYPE_CFG: Record<
  string,
  { label: string; short: string; icon: any; color: string; bg: string; ring: string }
> = {
  public_admin: {
    label: "Órgão público",
    short: "B2G",
    icon: Landmark,
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    ring: "ring-amber-200 dark:ring-amber-800",
  },
  empresa: {
    label: "Empresa",
    short: "B2B",
    icon: Building2,
    color: "text-sky-700 dark:text-sky-300",
    bg: "bg-sky-50 dark:bg-sky-950/40",
    ring: "ring-sky-200 dark:ring-sky-800",
  },
  cliente_final: {
    label: "Pessoa",
    short: "B2C",
    icon: Users,
    color: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    ring: "ring-emerald-200 dark:ring-emerald-800",
  },
};

function CrmOrganizationsContent() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [cityFilter, setCityFilter] = useState<string>("all");

  const query = trpc.crm.organizations.list.useQuery({
    search: debouncedSearch || undefined,
    contactType: typeFilter !== "all" ? typeFilter : undefined,
    state: stateFilter !== "all" ? stateFilter : undefined,
    city: cityFilter !== "all" ? cityFilter : undefined,
  });

  const rows = query.data?.rows ?? [];
  const states = query.data?.states ?? [];
  const cities = query.data?.cities ?? [];
  const totals = useMemo(() => {
    let contactCount = 0;
    let openDealCount = 0;
    let openValue = 0;
    for (const r of rows) {
      contactCount += r.contactCount;
      openDealCount += r.openDealCount;
      openValue += r.openValue;
    }
    return { contactCount, openDealCount, openValue };
  }, [rows]);

  const activeFilters =
    (typeFilter !== "all" ? 1 : 0) +
    (stateFilter !== "all" ? 1 : 0) +
    (cityFilter !== "all" ? 1 : 0) +
    (search ? 1 : 0);

  function clearFilters() {
    setSearch("");
    setTypeFilter("all");
    setStateFilter("all");
    setCityFilter("all");
  }

  return (
    <div className="space-y-5">
      {/* ── Header ─────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Organizações
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Clientes agrupados por órgão ou empresa — com todos os contatos e
              oportunidades envolvidas.
            </p>
          </div>
        </div>
      </div>

      {/* ── KPIs ───────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Organizações"
          value={String(rows.length)}
          icon={Building2}
          tint="primary"
        />
        <KpiCard
          label="Contatos totais"
          value={String(totals.contactCount)}
          icon={Users}
        />
        <KpiCard
          label="Oportunidades em aberto"
          value={String(totals.openDealCount)}
          icon={Briefcase}
        />
        <KpiCard
          label="Pipeline aberto"
          value={formatCompact(totals.openValue)}
          icon={TrendingUp}
          tint="emerald"
        />
      </div>

      {/* ── Busca ──────────────────────────────── */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input
          placeholder="Buscar organização pelo nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* ── Filtros (Tipo / Estado / Cidade) ────── */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Filter className="h-3.5 w-3.5" />
              Filtros
              {activeFilters > 0 && (
                <Badge variant="secondary" className="h-5 text-[10px]">
                  {activeFilters} ativo{activeFilters > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            {activeFilters > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={clearFilters}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Limpar
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Tipo de organização */}
            <div>
              <Label className="text-[11px] text-muted-foreground">
                Tipo
              </Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => setTypeFilter(v as TypeFilter)}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="public_admin">
                    <span className="flex items-center gap-2">
                      <Landmark className="h-3.5 w-3.5 text-amber-600" />
                      B2G · Órgãos públicos
                    </span>
                  </SelectItem>
                  <SelectItem value="empresa">
                    <span className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-sky-600" />
                      B2B · Empresas
                    </span>
                  </SelectItem>
                  <SelectItem value="cliente_final">
                    <span className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-emerald-600" />
                      B2C · Pessoas
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div>
              <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Estado
              </Label>
              <Select
                value={stateFilter}
                onValueChange={(v) => {
                  setStateFilter(v);
                  // Reseta cidade quando muda estado.
                  setCityFilter("all");
                }}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {states.map((uf: string) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cidade */}
            <div>
              <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Cidade
                {stateFilter !== "all" && (
                  <span className="text-[10px] text-muted-foreground ml-1">
                    (de {stateFilter})
                  </span>
                )}
              </Label>
              <Select
                value={cityFilter}
                onValueChange={setCityFilter}
                disabled={cities.length === 0}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue
                    placeholder={
                      cities.length === 0
                        ? "Nenhuma cidade"
                        : "Todas as cidades"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as cidades</SelectItem>
                  {cities.map((city: string) => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Lista ──────────────────────────────── */}
      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        // Backend pode lançar PRECONDITION_FAILED quando o tenant excede o cap
        // de contatos (server/db/crm-organizations.ts) — a mensagem do tRPC
        // já vem em PT-BR com instrução acionável ("use filtros…"). Sem este
        // branch, o usuário só via o empty state "Nenhuma organização" e
        // ficava sem entender o porquê.
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Não foi possível carregar as organizações</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{query.error?.message ?? "Erro desconhecido."}</p>
            <Button size="sm" variant="outline" onClick={() => query.refetch()}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold mb-1">Nenhuma organização</h3>
            <p className="text-sm text-muted-foreground">
              Cadastre contatos com empresa/órgão para agrupá-los aqui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <OrganizationRow key={r.name} org={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrganizationRow({ org }: { org: any }) {
  const type = org.contactType ?? "empresa";
  const cfg = TYPE_CFG[type] ?? TYPE_CFG.empresa;
  const TypeIcon = cfg.icon;
  const location = [org.city, org.state].filter(Boolean).join(" · ");
  const slug = encodeURIComponent(org.name);

  return (
    <Link
      href={`/crm/organizations/${slug}`}
      className="block group rounded-xl border bg-card p-4 transition hover:shadow-md hover:border-primary/40"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg} ${cfg.color} ring-1 ${cfg.ring}`}
        >
          <TypeIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-semibold truncate group-hover:text-primary">
              {org.name}
            </span>
            <Badge
              variant="outline"
              className={`text-[10px] h-5 ${cfg.bg} ${cfg.color} border-transparent`}
            >
              {cfg.short}
            </Badge>
          </div>
          {location && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {location}
            </div>
          )}
        </div>

        {/* Métricas à direita */}
        <div className="flex items-center gap-4 sm:gap-6 ml-auto flex-wrap">
          <Metric
            label="Contatos"
            value={String(org.contactCount)}
            icon={Users}
          />
          <Metric
            label="Em aberto"
            value={formatCompact(org.openValue)}
            icon={Briefcase}
            sub={`${org.openDealCount} deal${org.openDealCount === 1 ? "" : "s"}`}
            tint={org.openDealCount > 0 ? "primary" : "default"}
          />
          <Metric
            label="Fechados"
            value={formatCompact(org.wonValue)}
            icon={Trophy}
            sub={`${org.wonDealCount} deal${org.wonDealCount === 1 ? "" : "s"}`}
            tint={org.wonDealCount > 0 ? "emerald" : "default"}
          />
        </div>
      </div>
    </Link>
  );
}

function Metric({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "primary" | "emerald";
}) {
  const tintMap = {
    default: "text-muted-foreground",
    primary: "text-primary",
    emerald: "text-emerald-600",
  };
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className={`text-sm font-bold tabular-nums ${tintMap[tint]}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  icon: any;
  tint?: "default" | "primary" | "emerald";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">
              {value}
            </div>
          </div>
          <div
            className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}
          >
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmOrganizations() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:deals_view">
        <CrmOrganizationsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
